"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useTheme } from "../contexts/ThemeContext"
import axios from "axios"
import { getApiUrl, API_ENDPOINTS } from "../config/api"

function gtag_report_conversion(url) {
  if (typeof window.gtag === 'function') {
    var callback = function () {
      if (typeof(url) !== 'undefined') {
        window.location = url;
      }
    };
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17407909817/OrwLCOf9tv0aELm_3uxA',
      'value': 1.0,
      'currency': 'INR',
      'event_callback': callback
    });
  }
  return false;
}

function Subscription() {
  const [loading, setLoading] = useState(false)
  const { user, logout, refreshUser } = useAuth()
  const { theme } = useTheme();
  const navigate = useNavigate()

  const handleSubscribe = async (amount, planName, credits) => {
    gtag_report_conversion(); // Track conversion on buy button click
    setLoading(true)
    try {
      // Create order
      const orderResponse = await axios.post(getApiUrl(API_ENDPOINTS.PAYMENT.CREATE_ORDER), {
        amount,
        plan_name: planName,
        credits: credits
      })

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "Inflatein AI Studio",
        description: `${planName} Plan - ${credits} credits`,
        order_id: orderResponse.data.order_id,
        handler: async (response) => {
          // Payment successful
          console.log("Payment successful:", response)
          alert("Payment successful! Your credits have been added to your account.")

          // Refresh user data
          await refreshUser()

          // Redirect to dashboard
          navigate("/dashboard")
        },
        prefill: {
          email: user.email,
        },
        theme: {
          color: "#4F46E5",
        },
        modal: {
          ondismiss: () => {
            console.log("Payment modal closed")
          },
        },
      }

      const rzp = new window.Razorpay(options)
      rzp.on("payment.failed", (response) => {
        console.error("Payment failed:", response.error)
        alert(`Payment failed: ${response.error.description}`)
      })

      rzp.open()
    } catch (error) {
      console.error("Failed to create order:", error)
      alert("Failed to create order. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Calculate remaining credits and images
  const getRemainingCredits = () => {
    return typeof user?.credit_balance === "number" ? user.credit_balance : (user?.credit_balance || 0)
  }

  const getSubscriptionType = () => {
    return user?.subscription_type || (user?.subscription_status ? "pro" : "free")
  }

  const getRemainingImages = () => {
    const credits = getRemainingCredits()
    return Math.floor(credits / 10) // 10 credits = 1 image
  }

  return (
    <div className={theme === "dark" ? "min-h-screen bg-black text-white" : "min-h-screen bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100"}>
      <div className="max-w-4xl mx-auto py-14 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className={theme === "dark" ? "text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-100 via-gray-400 to-gray-700 drop-shadow" : "text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 drop-shadow"}>Choose Your Plan</h2>
          <p className={theme === "dark" ? "mt-4 text-lg text-gray-300" : "mt-4 text-lg text-gray-700"}>Unlock unlimited creativity with Inflatein AI Studio subscription plans</p>
          {getRemainingCredits() > 0 && (
            <div className={theme === "dark" ? "mt-4 p-4 bg-gray-800 border border-gray-700 rounded-xl shadow" : "mt-4 p-4 bg-green-50 border border-green-200 rounded-xl shadow"}>
              <p className={theme === "dark" ? "text-gray-200 font-medium" : "text-green-800 font-medium"}>
                🎉 You have {getRemainingCredits()} credits remaining! ({getRemainingImages()} images)
              </p>
            </div>
          )}
        </div>
        {/* Current Status */}
        <div className={theme === "dark" ? "bg-black border border-gray-800 rounded-2xl shadow-xl p-8 mb-12" : "bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-indigo-100 p-8 mb-12"}>
          <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-6 flex items-center gap-2" : "text-2xl font-bold text-indigo-700 mb-6 flex items-center gap-2"}>
            <svg className={theme === "dark" ? "w-6 h-6 text-gray-400" : "w-6 h-6 text-indigo-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
            Your Studio Stats
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className={theme === "dark" ? "text-3xl font-extrabold text-gray-300 drop-shadow" : "text-3xl font-extrabold text-indigo-600 drop-shadow"}>{user?.generation_count || 0}</div>
              <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Images Generated</div>
            </div>
            <div className="text-center">
              <div className={theme === "dark" ? "text-3xl font-extrabold text-gray-300 drop-shadow" : "text-3xl font-extrabold text-green-600 drop-shadow"}>{getRemainingCredits()}</div>
              <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Credits Remaining</div>
            </div>
            <div className="text-center">
              <div className={theme === "dark" ? "text-3xl font-extrabold text-gray-300 drop-shadow" : "text-3xl font-extrabold text-pink-600 drop-shadow"}>{getRemainingImages()}</div>
              <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Images Available</div>
            </div>
            <div className="text-center">
              <div className={theme === "dark" ? "text-3xl font-extrabold text-gray-300 drop-shadow capitalize" : "text-3xl font-extrabold text-indigo-500 drop-shadow capitalize"}>{getSubscriptionType()}</div>
              <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Subscription</div>
            </div>
          </div>
          <div className={theme === "dark" ? "mt-6 p-4 bg-gray-800 border border-gray-700 rounded-xl shadow" : "mt-6 p-4 bg-blue-50 rounded-xl shadow"}>
            <p className={theme === "dark" ? "text-sm text-gray-200 text-center" : "text-sm text-blue-800 text-center"}>
              💡 <strong>Credit System:</strong> 10 credits = 1 image generation
            </p>
          </div>
        </div>
        {/* Pricing Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Basic Plan */}
          <div className={theme === "dark" ? "border border-gray-700 rounded-2xl shadow-xl bg-black divide-y divide-gray-700 hover:scale-105 hover:shadow-2xl transition-transform duration-200" : "border border-gray-200 rounded-2xl shadow-xl bg-white/90 divide-y divide-gray-200 hover:scale-105 hover:shadow-2xl transition-transform duration-200"}>
            <div className="p-8">
              <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-indigo-700 mb-2"}>Basic Credits</h3>
              <p className={theme === "dark" ? "mt-2 text-sm text-gray-400" : "mt-2 text-sm text-gray-500"}>Perfect for occasional use</p>
              <p className="mt-8">
                <span className={theme === "dark" ? "text-4xl font-extrabold text-white" : "text-4xl font-extrabold text-gray-900"}>₹10</span>
                <span className={theme === "dark" ? "text-base font-medium text-gray-400" : "text-base font-medium text-gray-500"}> one-time</span>
              </p>
              <div className={theme === "dark" ? "mt-2 text-sm text-gray-300" : "mt-2 text-sm text-gray-600"}>
                <span className="font-medium">100 credits</span> • <span>10 images</span>
              </div>
              <button
                onClick={() => handleSubscribe(10, "Basic", 100)}
                disabled={loading}
                className={theme === "dark" ? "mt-8 block w-full bg-gray-800 border border-gray-700 rounded-xl py-3 text-base font-semibold text-white text-center hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200" : "mt-8 block w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 border border-transparent rounded-xl py-3 text-base font-semibold text-white text-center hover:from-indigo-600 hover:to-pink-600 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200"}
              >
                {loading ? "Processing..." : "Buy Credits"}
              </button>
            </div>
            <div className="pt-6 pb-8 px-8">
              <h4 className={theme === "dark" ? "text-xs font-medium text-gray-200 tracking-wide uppercase" : "text-xs font-medium text-gray-900 tracking-wide uppercase"}>What's included</h4>
              <ul className="mt-6 space-y-4">
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ 100 credits (10 images)</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ High quality images</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ No watermarks</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ Credits never expire</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• Basic plan uses default settings for image generation</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• No access to advanced controls (aspect ratio, quality, etc.)</span></li>
              </ul>
            </div>
          </div>
          {/* Pro Plan */}
          <div className={theme === "dark" ? "border-2 border-gray-600 rounded-2xl shadow-xl bg-black divide-y divide-gray-700 relative hover:scale-105 hover:shadow-2xl transition-transform duration-200" : "border-2 border-indigo-200 rounded-2xl shadow-xl bg-white/90 divide-y divide-gray-200 relative hover:scale-105 hover:shadow-2xl transition-transform duration-200"}>
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <span className={theme === "dark" ? "bg-gray-800 text-white px-4 py-1 text-sm font-medium rounded-full shadow border border-gray-700" : "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-4 py-1 text-sm font-medium rounded-full shadow"}>Most Popular</span>
            </div>
            <div className="p-8">
              <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-2" : "text-2xl font-bold text-pink-700 mb-2"}>Pro Credits</h3>
              <p className={theme === "dark" ? "mt-2 text-gray-400" : "mt-2 text-gray-500"}>Best for power users and professionals</p>
              <p className="mt-8">
                <span className={theme === "dark" ? "text-4xl font-extrabold text-white" : "text-4xl font-extrabold text-gray-900"}>₹299</span>
                <span className={theme === "dark" ? "text-base font-medium text-gray-400" : "text-base font-medium text-gray-500"}> one-time</span>
              </p>
              <div className={theme === "dark" ? "mt-2 text-sm text-gray-300" : "mt-2 text-sm text-gray-600"}>
                <span className="font-medium">5000 credits</span> • <span>500 images</span>
              </div>
              <button
                onClick={() => handleSubscribe(299, "Pro", 5000)}
                disabled={loading}
                className={theme === "dark" ? "mt-8 block w-full bg-gray-800 border border-gray-700 rounded-xl py-3 text-base font-semibold text-white text-center hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200" : "mt-8 block w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 border border-transparent rounded-xl py-3 text-base font-semibold text-white text-center hover:from-pink-600 hover:to-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200"}
              >
                {loading ? "Processing..." : "Buy Pro Credits"}
              </button>
            </div>
            <div className="pt-6 pb-8 px-8">
              <h4 className={theme === "dark" ? "text-xs font-medium text-gray-200 tracking-wide uppercase" : "text-xs font-medium text-gray-900 tracking-wide uppercase"}>What's included</h4>
              <ul className="mt-6 space-y-4">
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ 5000 credits (500 images)</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ Highest quality images</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ Priority support</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ No watermarks</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ Credits never expire</span></li>
                <li className="flex space-x-3"><span className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>✓ Access to advanced image controls:</span></li>
                <ul className="ml-6 space-y-2">
                  <li className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• Set aspect ratio</li>
                  <li className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• Choose image quality</li>
                  <li className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• Select output format (JPG, PNG, WEBP)</li>
                  <li className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• Control inference steps</li>
                  <li className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>• Faster image generation (Go Fast mode)</li>
                </ul>
              </ul>
            </div>
          </div>
        </div>
        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-200 mb-8" : "text-2xl font-bold text-indigo-700 mb-8"}>Frequently Asked Questions</h3>
          <div className="space-y-6">
            <div className={theme === "dark" ? "bg-black border border-gray-700 p-6 rounded-xl shadow" : "bg-white/90 p-6 rounded-xl border shadow"}>
              <h4 className={theme === "dark" ? "font-medium text-gray-200" : "font-medium text-gray-900"}>How does the credit system work?</h4>
              <p className={theme === "dark" ? "text-sm text-gray-400 mt-2" : "text-sm text-gray-600 mt-2"}>
                Each image generation costs 10 credits. You can buy credits in bulk or subscribe pro for regular usage.
              </p>
            </div>
            <div className={theme === "dark" ? "bg-black border border-gray-700 p-6 rounded-xl shadow" : "bg-white/90 p-6 rounded-xl border shadow"}>
              <h4 className={theme === "dark" ? "font-medium text-gray-200" : "font-medium text-gray-900"}>Do credits expire?</h4>
              <p className={theme === "dark" ? "text-sm text-gray-400 mt-2" : "text-sm text-gray-600 mt-2"}>
                No, credits never expire. You can use them anytime, even after months of inactivity.
              </p>
            </div>
            <div className={theme === "dark" ? "bg-black border border-gray-700 p-6 rounded-xl shadow" : "bg-white/90 p-6 rounded-xl border shadow"}>
              <h4 className={theme === "dark" ? "font-medium text-gray-200" : "font-medium text-gray-900"}>Can I cancel my Pro Credits purchase?</h4>
              <p className={theme === "dark" ? "text-sm text-gray-400 mt-2" : "text-sm text-gray-600 mt-2"}>
                Pro Credits are a one-time purchase and cannot be refunded once the credits are added to your account. However, credits never expire.
              </p>
            </div>
            <div className={theme === "dark" ? "bg-black border border-gray-700 p-6 rounded-xl shadow" : "bg-white/90 p-6 rounded-xl border shadow"}>
              <h4 className={theme === "dark" ? "font-medium text-gray-200" : "font-medium text-gray-900"}>What payment methods do you accept?</h4>
              <p className={theme === "dark" ? "text-sm text-gray-400 mt-2" : "text-sm text-gray-600 mt-2"}>
                We accept all major credit cards, debit cards, UPI, and net banking through Razorpay.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Subscription
